import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Award, BarChart2, Bell, CreditCard, Flame, Gem, Globe, GraduationCap, Home, LayoutDashboard, Library, LogOut, Settings, ShieldCheck, Sparkles, Trophy } from "lucide-react";
import { AppState } from "@/types/linguadeck";
import NotificationsPopover from "./NotificationsPopover";
import { useAuth } from "@/contexts/AuthContext";
import { useGamification } from "@/contexts/GamificationContext";
import { useNotificationStore, type Notification } from "@/stores/useNotificationStore";
import { useGamificationStore, type AchievementsTabId } from "@/stores/useGamificationStore";
import { useTranslation } from "react-i18next";
import NewDeckDropdown from "./NewDeckDropdown";
import type { DeckInitialType } from "./DeckModal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
type DeckDashboardModeKey = "cards" | "learn" | "test" | "games";

interface TopbarProps {
  currentView: AppState["currentView"];
  currentDeckTitle?: string;
  /** When true, show "Dashboard" title and LayoutDashboard icon (deck dashboard mode) */
  showDeckDashboardHeader?: boolean;
  /** Current study mode for contextual header: "Dashboard: Flashcards" etc. */
  deckDashboardMode?: DeckDashboardModeKey;
  /** When true and currentView is library, show "Global Library" instead of "My Library" */
  isGlobalLibrary?: boolean;
  isTeacher: boolean;
  points: number;
  onOpenSettings: () => void;
  onOpenDraw: () => void;
  onNewDeck: (type: DeckInitialType) => void;
  onCreateFolder: () => void;
  onNavTo: (view: AppState["currentView"]) => void;
  onOpenAchievements: () => void;
}

const DECK_DASHBOARD_MODE_LABELS: Record<DeckDashboardModeKey, string> = {
  cards: "deckModal.studyModeCards",
  learn: "deckModal.studyModeLearn",
  test: "deckModal.studyModeTest",
  games: "studyMenu.games",
};

export default function Topbar({
  currentView,
  currentDeckTitle,
  showDeckDashboardHeader = false,
  deckDashboardMode,
  isGlobalLibrary = false,
  isTeacher,
  points,
  onOpenSettings,
  onOpenDraw,
  onNewDeck,
  onCreateFolder,
  onNavTo,
  onOpenAchievements,
}: TopbarProps) {
  const { user, profile, avatarUrl, signOut, isAdminUser, isAdminMode, setIsAdminMode } = useAuth();
  const navigate = useNavigate();
  const { crystals, currentStreak: streak } = useGamification();
  const { t } = useTranslation();
  const notifications = useNotificationStore((s) => s.notifications);
  const unreadCount = useNotificationStore((s) => s.unreadCount);
  const markAsRead = useNotificationStore((s) => s.markAsRead);
  const markAllAsRead = useNotificationStore((s) => s.markAllAsRead);
  const removeNotification = useNotificationStore((s) => s.removeNotification);
  const hydrateForUser = useNotificationStore((s) => s.hydrateForUser);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  useEffect(() => {
    hydrateForUser(user?.id ?? null);
  }, [user?.id, hydrateForUser]);

  const handleNotificationClick = (notification: Notification) => {
    setNotificationsOpen(false);
    markAsRead(notification.id);
    if (notification.displayType === "ROUTE") {
      navigate(notification.target);
    } else if (notification.displayType === "MODAL") {
      const tab = notification.target as AchievementsTabId;
      navigate(tab ? `/gamification?tab=${tab}` : "/gamification");
    }
  };

  const viewTitle: Record<AppState["currentView"], string> = {
    home: t("sidebar.home"),
    library: isGlobalLibrary ? t("sidebar.globalLibrary") : t("sidebar.mySets"),
    flashcards: currentDeckTitle ? currentDeckTitle : t("navigation.flashcards"),
    test: currentDeckTitle ? currentDeckTitle : t("navigation.test"),
    upload: t("navigation.aiGenerator"),
    dashboard: t("statistics.pageTitle"),
    gamification: t("header.achievements"),
  };

  const displayTitle = showDeckDashboardHeader
    ? (deckDashboardMode ? t("deckDashboard.titleWithMode", { mode: t(DECK_DASHBOARD_MODE_LABELS[deckDashboardMode]) }) : t("deckDashboard.title"))
    : viewTitle[currentView];
  const ViewIcon = ({ view }: { view: AppState["currentView"] }) => {
    const cls = "w-4 h-4 shrink-0 sm:w-5 sm:h-5";
    if (showDeckDashboardHeader) {
      return <LayoutDashboard className={`${cls} text-indigo-500`} />;
    }
    switch (view) {
      case "home":
        return <Home className={`${cls} text-blue-500`} />;
      case "library":
        return isGlobalLibrary ? <Globe className={`${cls} text-indigo-500`} /> : <Library className={`${cls} text-purple-600`} />;
      case "dashboard":
        return <BarChart2 className={`${cls} text-indigo-500`} />;
      case "gamification":
        return <Award className={`${cls} text-purple-500`} />;
      case "upload":
        return <Sparkles className={`${cls} text-amber-500`} />;
      case "flashcards":
        return <Trophy className={`${cls} text-emerald-600`} />;
      case "test":
        return <CreditCard className={`${cls} text-sky-500`} />;
      default:
        return <Home className={`${cls} text-gray-500`} />;
    }
  };

  return (
    <header
      className="flex w-full flex-col gap-2 bg-card shrink-0 overflow-visible pl-[max(0.75rem,env(safe-area-inset-left))] pr-[max(0.75rem,env(safe-area-inset-right))] pt-[max(0.75rem,env(safe-area-inset-top))] pb-2.5 md:flex-row md:items-center md:justify-between md:gap-3 md:px-4 md:py-3"
    >
      {/* Left: Hamburger + Title */}
      <div className="flex min-w-0 w-full items-center gap-2 overflow-hidden md:w-auto md:flex-1">
        <button
          onClick={onOpenDraw}
          className="md:hidden flex items-center justify-center shrink-0 rounded-lg p-1.5 text-[hsl(var(--foreground))]"
          style={{
            background: "transparent",
            border: "none",
            fontSize: 20,
            cursor: "pointer",
          }}
        >
          ☰
        </button>
        <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
          <ViewIcon view={currentView} />
          <span className="truncate text-sm font-semibold text-[hsl(var(--foreground))] md:text-[15px]" title={displayTitle}>
            {displayTitle}
          </span>
        </div>
      </div>

      {/* Right: Stats + Actions. Mobile gets a second compact row; desktop stays inline. */}
      <div className="grid w-full min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-2 md:flex md:w-auto md:shrink-0 md:items-center md:justify-end md:gap-3">
        <div className="flex min-w-0 items-center gap-1.5 sm:gap-2 md:flex-none md:gap-3">
          <NotificationsPopover
            notifications={notifications}
            onDeleteNotification={removeNotification}
            onMarkAsRead={markAsRead}
            onMarkAllAsRead={markAllAsRead}
            onNotificationClick={handleNotificationClick}
            open={notificationsOpen}
            onOpenChange={setNotificationsOpen}
            trigger={
            <button
              className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/10 sm:h-9 sm:w-9 md:h-10 md:w-10"
              title={t("notifications.title")}
              type="button"
            >
              <Bell size={18} strokeWidth={2} className="h-[17px] w-[17px] sm:h-[18px] sm:w-[18px] md:h-5 md:w-5" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-semibold border-2 border-[hsl(var(--bg2))]">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </button>
          }
            side="bottom"
          />
          {/* Achievements / Balance button — compact on mobile */}
          <button
            onClick={onOpenAchievements}
            className="flex min-w-0 shrink-0 items-center justify-center gap-1 overflow-hidden rounded-full px-2 py-1 text-[10px] font-semibold transition-all sm:gap-1.5 sm:px-2.5 sm:py-1.5 sm:text-xs md:flex-none md:gap-2 md:px-3 md:py-2"
            style={{
              background: "rgba(0,229,160,.1)",
              border: "1px solid rgba(0,229,160,.25)",
              color: "hsl(var(--accent2))",
              fontFamily: "DM Sans, sans-serif",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(0,229,160,.18)";
              e.currentTarget.style.borderColor = "rgba(0,229,160,.4)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(0,229,160,.1)";
              e.currentTarget.style.borderColor = "rgba(0,229,160,.25)";
            }}
          >
            <span className="flex min-w-0 items-center gap-1 truncate text-slate-800 dark:text-slate-200 font-semibold">
              <Gem className="h-3 w-3 shrink-0 text-sky-500 sm:h-3.5 sm:w-3.5 md:h-4 md:w-4" />
              <span className="truncate">{crystals.toLocaleString()}</span>
            </span>
            {streak > 0 && (
              <>
                <div className="h-2.5 w-px shrink-0 bg-gray-300 dark:bg-gray-600 sm:h-3 md:h-4" />
                <span className="flex shrink-0 items-center gap-0.5 sm:gap-1 md:gap-1.5" style={{ color: "hsl(var(--accent3))" }}>
                  <Flame className="h-3 w-3 shrink-0 text-orange-500 sm:h-3.5 sm:w-3.5 md:h-4 md:w-4" />
                  {streak}
                </span>
              </>
            )}
          </button>
        </div>

        <div className="flex shrink-0 items-center justify-end gap-1.5 sm:gap-2">
          {currentView !== "test" && currentView !== "flashcards" && (
            <div className="relative z-30 pointer-events-auto touch-manipulation">
              <NewDeckDropdown
                dropdownId="header-new-deck"
                onSelect={onNewDeck}
                onCreateFolder={onCreateFolder}
                variant="icon"
                menuAlign="end"
                className="relative z-30 pointer-events-auto touch-manipulation"
                triggerStyle={{
                  width: 36,
                  height: 36,
                  background: "rgb(99, 102, 241)",
                  color: "white",
                  border: "none",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.16)",
                  pointerEvents: "auto",
                  touchAction: "manipulation",
                  WebkitTapHighlightColor: "transparent",
                }}
              />
            </div>
          )}

          {/* User avatar dropdown */}
          {user && (
            <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: "50%",
                  overflow: "hidden",
                  flexShrink: 0,
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  background:
                    avatarUrl
                      ? "transparent"
                      : "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--accent2)))",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                title={user.email ?? ""}
              >
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt=""
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <span
                    style={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: "white",
                    }}
                  >
                    {(profile?.username ?? user.email ?? "U").toString().charAt(0).toUpperCase()}
                  </span>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-56 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-800 shadow-lg rounded-md"
            >
              <DropdownMenuLabel className="text-slate-900 dark:text-slate-100 font-semibold">
                <div className="flex flex-col gap-0.5">
                  <span>
                    {user.user_metadata?.full_name ??
                      user.user_metadata?.name ??
                      user.email ??
                      "User"}
                  </span>
                  {user.email && (
                    <span className="text-xs text-slate-500 dark:text-slate-400 font-normal">
                      {user.email}
                    </span>
                  )}
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-slate-200 dark:bg-slate-700" />
              {isAdminUser ? (
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    setIsAdminMode(!isAdminMode);
                  }}
                  className="cursor-pointer text-slate-700 dark:text-slate-300 hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-white focus:bg-slate-100 focus:text-slate-900 dark:focus:bg-slate-800 dark:focus:text-white data-[highlighted]:bg-slate-100 data-[highlighted]:text-slate-900 dark:data-[highlighted]:bg-slate-800 dark:data-[highlighted]:text-white"
                >
                  <div className="flex items-center gap-2">
                    {isAdminMode ? (
                      <>
                        <ShieldCheck className="w-4 h-4 text-amber-500 shrink-0" />
                        {t("roles.admin", "Admin")}
                      </>
                    ) : (
                      <>
                        <GraduationCap className="w-4 h-4 text-indigo-500 shrink-0" />
                        {isTeacher ? t("roles.teacher") : t("roles.student")}
                      </>
                    )}
                  </div>
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  disabled
                  className="cursor-default opacity-100 text-slate-700 dark:text-slate-300 focus:!bg-transparent data-[highlighted]:!bg-transparent"
                >
                  <div className="flex items-center gap-2">
                    <GraduationCap className="w-4 h-4 text-indigo-500 shrink-0" />
                    {isTeacher ? t("roles.teacher") : t("roles.student")}
                  </div>
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator className="bg-slate-200 dark:bg-slate-700" />
              <DropdownMenuItem
                onClick={onOpenSettings}
                className="cursor-pointer text-slate-700 dark:text-slate-300 hover:bg-slate-100 hover:text-slate-900 focus:bg-slate-100 focus:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-white dark:focus:bg-slate-800 dark:focus:text-white data-[highlighted]:bg-slate-100 data-[highlighted]:text-slate-900 dark:data-[highlighted]:bg-slate-800 dark:data-[highlighted]:text-white"
              >
                <Settings size={16} className="mr-2" />
                {t("buttons.settings")}
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-slate-200 dark:bg-slate-700" />
              <DropdownMenuItem
                onClick={() => signOut()}
                className="cursor-pointer text-red-600 dark:text-red-400 hover:bg-red-50 hover:!text-red-700 focus:bg-red-50 focus:!text-red-700 dark:hover:bg-slate-800 dark:hover:!text-red-300 dark:focus:bg-slate-800 dark:focus:!text-red-300 data-[highlighted]:bg-red-50 data-[highlighted]:!text-red-700 dark:data-[highlighted]:bg-slate-800 dark:data-[highlighted]:!text-red-300"
              >
                <LogOut size={16} className="mr-2" />
                {t("buttons.signOut")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          )}
        </div>
      </div>
    </header>
  );
}
